/**
 * Pure patient filter logic shared by the web worker (SHER-327 trims search input).
 */
import { PAGINATION_CONFIG } from '../config/pagination';
import { areSameDates } from '../utils/birthdate';
import { phoneDigits } from '../utils/patientDedup';
import { resolvePersonalFieldRoles } from '../utils/personalFieldRoles';
import { trimSearchValues } from '../utils/trimSearchValue';

export interface PatientFilterInput {
    patients: any[];
    searchValues: Record<string, string>;
    personalFields: { name: string; options: any[]; label?: string; type?: string }[];
    includeDeceased?: boolean;
    hasDeceasedWorkflow?: boolean;
    appointmentTodayUuids?: string[] | null;
}

export interface PatientFilterOutput {
    patients: any[];
    truncated?: boolean;
}

export function filterPatients(input: PatientFilterInput): PatientFilterOutput {
    const {
        patients,
        searchValues: rawSearchValues,
        personalFields,
        includeDeceased,
        hasDeceasedWorkflow,
        appointmentTodayUuids,
    } = input;

    const searchValues = trimSearchValues(rawSearchValues);
    const maxResults = PAGINATION_CONFIG.MAX_PATIENT_SEARCH_RESULTS;
    const todaySet = appointmentTodayUuids ? new Set(appointmentTodayUuids) : null;
    // SHER-579: phone compare is digit-only (ciphertext cannot be SQL-sanitized).
    const phoneFieldNames = new Set(resolvePersonalFieldRoles(personalFields).phone);

    if (searchValues.hasOwnProperty('id')) {
        const idVal = parseInt(searchValues['id']);
        const result = patients.filter((p) => p.id === idVal && (!todaySet || todaySet.has(p.uuid)));
        return { patients: result };
    }

    const searchKeys = Object.keys(searchValues);
    const fieldMap = new Map<string, { options: any[] }>();
    for (const pf of personalFields) {
        fieldMap.set(pf.name, { options: pf.options || [] });
    }

    const loweredValues: Record<string, string> = {};
    const phoneQueryDigits: Record<string, string> = {};
    for (const key of searchKeys) {
        if (key === 'birthdate' || key === 'automated_health_id') continue;
        if (fieldMap.get(key)?.options?.length) continue;
        const v = searchValues[key];
        if (phoneFieldNames.has(key)) {
            phoneQueryDigits[key] = phoneDigits(v);
            continue;
        }
        loweredValues[key] = v === '' ? '' : String(v).toLowerCase();
    }

    const results: any[] = [];

    for (const patient of patients) {
        const pd = patient.personalData;
        if (!pd) continue;

        let match = true;

        for (const key of searchKeys) {
            const value = searchValues[key];
            const field = fieldMap.get(key);

            if (field && field.options.length > 0) {
                if (value !== '' && pd[key] !== value) {
                    match = false;
                    break;
                }
            } else if (key === 'birthdate') {
                if (value === '') continue;
                // SHER-685: compare calendar day via birthdateToDate (YYYY-MM-DD and
                // legacy ms-epoch digit strings). Bare `new Date(msString)` is Invalid Date.
                if (!areSameDates(value, pd['birthdate'])) {
                    match = false;
                    break;
                }
            } else if (key === 'automated_health_id') {
                if (value !== '') {
                    const patientMrn = pd['automated_health_id'] == null ? '' : String(pd['automated_health_id']).trim();
                    if (patientMrn !== value) {
                        match = false;
                        break;
                    }
                }
            } else if (phoneFieldNames.has(key)) {
                // SHER-579: compare digits only so "77 289 86 43" ↔ "772898643".
                if (value === '') continue;
                const queryDigits = phoneQueryDigits[key];
                if (!queryDigits) {
                    match = false;
                    break;
                }
                const storedDigits = phoneDigits(pd[key]);
                if (!storedDigits || !storedDigits.includes(queryDigits)) {
                    match = false;
                    break;
                }
            } else {
                if (value !== '') {
                    const patientVal = pd[key];
                    if (patientVal == null || patientVal === '') {
                        match = false;
                        break;
                    }
                    if (!String(patientVal).toLowerCase().includes(loweredValues[key])) {
                        match = false;
                        break;
                    }
                }
            }
        }

        if (match && hasDeceasedWorkflow && !includeDeceased && patient.status === 4) {
            match = false;
        }

        if (match && todaySet && !todaySet.has(patient.uuid)) {
            match = false;
        }

        if (match) results.push(patient);
        if (results.length >= maxResults) break;
    }

    const truncated = results.length >= maxResults;
    results.sort((a, b) => b.id - a.id);
    return { patients: results, truncated };
}
